/**
 * Simulation types.
 *
 * Coordinate system: x increases right, **y increases downward**, one tile = one
 * world unit. Tile (tx, ty) spans x in [tx, tx+1) and y in [ty, ty+1), so the
 * grid row index and the world y coordinate are the same number. The renderer
 * negates y; nothing else in the codebase has to think about it.
 *
 * The player's position is (x = centre, y = feet).
 */

export const Tile = {
  Empty: 0,
  Stone: 1, // permanent, cannot be cast away
  Seed: 2, // player-created, castable
  Vine: 3, // authored but castable
} as const;
export type TileKind = (typeof Tile)[keyof typeof Tile];

export const Phase = {
  Settled: 0,
  Spawning: 1, // created but not yet solid (CAST_SPAWN_TIME)
  Crumbling: 2, // destroyed but not yet gone (CRUMBLE_TIME)
} as const;
export type PhaseKind = (typeof Phase)[keyof typeof Phase];

export const Btn = {
  Left: 1,
  Right: 2,
  Jump: 4,
  Cast: 8,
  Ranged: 16,
  /** Held modifier. Redirects a cast from the facing tile to the tile underfoot. */
  Down: 32,
  /** Edge-triggered: enters an open win portal or activates a door underfoot/
   *  overlapping. Destinations are picked deliberately, never entered merely
   *  by walking through their tile. */
  Activate: 64,
} as const;
/** One frame of input, as a bitmask of Btn values. */
export type InputFrame = number;

export const ItemKind = {
  Moonseed: 'moonseed',
  Sprite: 'sprite',
  Lumen: 'lumen',
  Moonshard: 'moonshard',
  TailCharm: 'tailcharm',
  /** Single-use. Destroys every living Warden in the room on pickup - the
   *  non-aimed counterpart to the Tail skill (brief §5, "killed by a skill or
   *  an item"). No charges, no aiming: pick it up and Wardens are gone. */
  Sunshard: 'sunshard',
} as const;
export type ItemKindName = (typeof ItemKind)[keyof typeof ItemKind];

export const ChimeraKind = {
  Creep: 'creep',
  Drifter: 'drifter',
  Sower: 'sower',
  /**
   * Touch kills the player like any Chimera, and it is blocked by a cast block
   * in its path exactly like a Drifter. Casting a block never kills any
   * Chimera kind (only the ranged Tail skill and the Sunshard item do), so a
   * Warden is not defined by that - it is defined by never routing around an
   * obstacle (no altitude change) and never being clearable by a jump.
   */
  Warden: 'warden',
} as const;
export type ChimeraKindName = (typeof ChimeraKind)[keyof typeof ChimeraKind];

export interface Player {
  x: number;
  y: number;
  vx: number;
  vy: number;
  facing: 1 | -1;
  grounded: boolean;
  coyote: number;
  jumpBuffer: number;
  castCooldown: number;
  rangedCooldown: number;
  prevButtons: InputFrame;
  hasMoonseed: boolean;
  rangedCharges: number;
}

export interface Chimera {
  id: number;
  kind: ChimeraKindName;
  /** Creep: continuous (centre, feet). Drifter/Sower: tile-aligned. */
  x: number;
  y: number;
  vy: number;
  dir: 1 | -1;
  timer: number;
  alive: boolean;
}

export interface Item {
  id: number;
  kind: ItemKindName;
  tx: number;
  ty: number;
  /** Hidden items only become collectable once the block in their tile is gone. */
  hidden: boolean;
  taken: boolean;
}

/**
 * A room-to-room exit, distinct from the win-condition `portal` above.
 *
 * Built for the Grove hub: a door sits at a fixed tile (often floating,
 * reachable only by casting a block staircase up to it, same skill every
 * room already teaches) and, activated (not merely walked into - see
 * `Btn.Activate`) while unlocked, switches the active room outright - no
 * moonseed, no "cleared" outcome, no solvability-harness obligation.
 * `locked` is authored per visit by whoever builds the room (the hub
 * recomputes it from cleared-room progress each time it's (re)entered); the
 * simulation itself has no opinion on why a door is locked.
 */
export interface Door {
  id: number;
  tx: number;
  ty: number;
  targetRoomId: string;
  locked: boolean;
  /** What the door displays (e.g. a room number). Purely cosmetic. */
  label: string;
  /** Buried under a cast block on its own tile - same mechanic as a hidden
   *  item (`Item.hidden`), generalized (brief pillar 5 direct request): not
   *  rendered, no `doorPrompt`, cannot be activated, until the covering
   *  block is destroyed. */
  hidden: boolean;
}

/** Whichever door the player is currently in range to activate, if any -
 *  purely so the HUD can prompt "press E" (brief pillar 5: never a control
 *  with no on-screen affordance). */
export interface DoorPrompt {
  label: string;
  locked: boolean;
}

/**
 * Which unlocked Axie parts change the sim's verbs this run (brief §7, §8).
 * Never required to solve a room - only the Starter loadout (every field
 * false) is what the CI solvability harness and every recorded solution
 * replay against, so a new field here must only ever add routes.
 */
export interface Loadout {
  /** Bypasses the Tail skill's ammo gate (`fireRanged`), so the ranged
   *  fireball fires even at zero `rangedCharges` - the only way a Round 1
   *  player can use it at all, since no Round 1 room grants a TailCharm.
   *  Does not change `doCast` in any way. */
  brambleHorn: boolean;
}

/** What every room starts with absent an unlock - also what the solvability
 *  harness and every recorded solution run against, by construction. */
export const STARTER_LOADOUT: Loadout = { brambleHorn: false };

export const Outcome = {
  Playing: 'playing',
  Cleared: 'cleared',
  Failed: 'failed',
} as const;
export type OutcomeName = (typeof Outcome)[keyof typeof Outcome];

export const FailReason = {
  None: 'none',
  Moonlight: 'moonlight',
  Chimera: 'chimera',
  Void: 'void',
} as const;
export type FailReasonName = (typeof FailReason)[keyof typeof FailReason];

/** Emitted per tick for audio/FX. The simulation never reads these back. */
export type SimEvent =
  | { type: 'cast:create'; tx: number; ty: number }
  | { type: 'cast:destroy'; tx: number; ty: number }
  | { type: 'cast:refused'; tx: number; ty: number }
  | { type: 'cast:stone'; tx: number; ty: number }
  | { type: 'block:hit'; tx: number; ty: number; hits: number }
  | { type: 'chimera:banished'; id: number; cause: 'ranged' | 'item' }
  /** `tx`/`ty` is the first tile the shot enters (one ahead of the player,
   *  on the feet row); `tiles` is how far it actually travelled before
   *  stopping - a wall, the max range, or a kill all cut it short - and
   *  `facing` says which way from `tx` those tiles extend. Lets a renderer
   *  draw the real beam instead of guessing its length or direction. */
  | { type: 'ranged:fired'; tx: number; ty: number; tiles: number; facing: 1 | -1 }
  | { type: 'ranged:empty' }
  | { type: 'player:jump' }
  | { type: 'player:land' }
  | { type: 'item:taken'; kind: ItemKindName; id: number }
  | { type: 'door:entered'; targetRoomId: string }
  | { type: 'door:locked'; targetRoomId: string }
  | { type: 'item:hidden'; id: number }
  | { type: 'item:revealed'; id: number }
  | { type: 'door:hidden'; id: number }
  | { type: 'door:revealed'; id: number }
  | { type: 'portal:hidden' }
  | { type: 'portal:revealed' }
  | { type: 'portal:open' }
  | { type: 'room:cleared' }
  | { type: 'room:failed'; reason: FailReasonName };

export interface SimState {
  tick: number;
  tiles: Uint8Array; // GRID_W * GRID_H, TileKind
  phase: Uint8Array; // GRID_W * GRID_H, PhaseKind
  phaseTimer: Float64Array;
  /** Head-butts landed on each tile from below, cleared when the tile changes. */
  hits: Uint8Array;
  player: Player;
  chimera: Chimera[];
  items: Item[];
  doors: Door[];
  doorPrompt: DoorPrompt | null;
  /** True only while the player overlaps a visible, Moonseed-open portal.
   *  Kept in sim state so the HUD can give the E-key affordance without
   *  duplicating collision logic in React. */
  portalPrompt: boolean;
  /** Same hidden mechanic as items/doors: buried under a cast block, this
   *  never clears the room even while the player holds the Moonseed and
   *  stands on its tile - which cannot actually happen while a block is
   *  solid there anyway, but the flag also drives whether it renders. */
  portal: { tx: number; ty: number; hidden: boolean };
  moonlight: number;
  outcome: OutcomeName;
  failReason: FailReasonName;
  nextId: number;
}

export interface RoomDef {
  id: string;
  name: string;
  /** GRID_H rows of GRID_W characters. '#' stone, '~' vine, '.' empty. */
  map: readonly string[];
  spawn: { x: number; y: number };
  portal: { tx: number; ty: number; hidden?: boolean };
  items: ReadonlyArray<{
    kind: ItemKindName;
    tx: number;
    ty: number;
    hidden?: boolean;
  }>;
  chimera: ReadonlyArray<{ kind: ChimeraKindName; x: number; y: number; dir?: 1 | -1 }>;
  /** Room-to-room exits (the Grove hub). Empty/absent for every puzzle room -
   *  those use `portal` instead. */
  doors?: ReadonlyArray<{
    tx: number;
    ty: number;
    targetRoomId: string;
    locked?: boolean;
    label?: string;
    hidden?: boolean;
  }>;
  moonlight?: number;
  /** Skips the pre-room "plan before you start" countdown (`useGame`). Only
   *  the Grove hub sets this - every puzzle room gets the countdown by
   *  default, no per-room authoring needed. */
  skipCountdown?: boolean;
}
